import UserAvatar from "@/components/controls/UserAvatar";
import Text from "@/components/ui/text";
import { find, groupBy, indexOf, isEmpty, keys, map, uniqueId } from "lodash";
import React, { memo, useCallback, useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip as RechartsTooltip,
  TooltipProps
} from "recharts";
import {
  NameType,
  ValueType
} from "recharts/types/component/DefaultTooltipContent";
import { useWinRateBreadcrumbs } from "@/components/layouts/WinRate/WinRateBreadcrumbsContext";
import { CustomTooltip } from "./Tooltip";
import { getCurrentLocale } from "@/utils/locale";
import { WinRateDrilldownChartDataType } from "./WinRateDrilldownContent";
import { salesPipelineColors } from "@/components/layouts/SalesPipeline/SalesColors";

interface Props {
  height: number;
}

const AVATAR_HEIGHT = 24;

const DrilldownChart: React.FC<Props> = memo(({ height }) => {
  const { dynamicTableData, directReportsData } = useWinRateBreadcrumbs();
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const chartData = useMemo(() => {
    if (!directReportsData?.overTime?.toMonth) return [];
    return Object.entries(directReportsData.overTime.toMonth).map(
      ([date, values]) => ({
        date: new Date(date).toLocaleDateString(getCurrentLocale(), {
          month: "2-digit",
          year: "2-digit"
        }),
        ...values
      })
    );
  }, [directReportsData]);

  const flattenedChartData = useMemo(
    () =>
      chartData.flatMap((item) =>
        Object.entries(item)
          .filter(([key]) => key !== "date")
          .map(([, value]) => Number(value))
      ),
    [chartData]
  );

  const generateYAxisTicks = (minValue: number, maxValue: number): number[] => {
    if (maxValue <= 0 || maxValue - minValue < 1) {
      return [0, 25, 50, 75, 100];
    }

    // Round down min and up max to nearest 5
    const roundedMin = Math.floor(minValue / 5) * 5;
    const roundedMax = Math.ceil(maxValue / 5) * 5;

    // Create 5 evenly spaced ticks
    const range = roundedMax - roundedMin;
    const step = range / 4;

    return [
      roundedMin,
      roundedMin + step,
      roundedMin + 2 * step,
      roundedMin + 3 * step,
      roundedMax
    ];
  };

  const maxValue = useMemo(
    () => Math.max(...flattenedChartData),
    [flattenedChartData]
  );
  const minValue = useMemo(
    () => Math.min(...flattenedChartData),
    [flattenedChartData]
  );
  const yAxisTicks = useMemo(
    () => generateYAxisTicks(minValue, maxValue),
    [minValue, maxValue]
  );
  const lowerBound = useMemo(() => yAxisTicks[0], [yAxisTicks]);
  const upperBound = useMemo(() => yAxisTicks[4], [yAxisTicks]);

  const handleMouseMove = useCallback((e: any) => {
    if (e && e.activeTooltipIndex !== undefined) {
      setTooltipPos({ x: e.activeCoordinate.x - 85, y: 10 });
    }
  }, []);

  const calculateHeight = (value: number) => {
    if (minValue === maxValue && minValue === 0) {
      return `${AVATAR_HEIGHT}px`;
    }

    const chartHeight = height;
    const pointY = value - lowerBound;
    const yAxis = upperBound - lowerBound;
    const ratio = pointY / yAxis;
    const px = ratio * chartHeight + AVATAR_HEIGHT * 0.45;
    return `${px}px`;
  };

  const renderIcons = useMemo(() => {
    const lastPoint = chartData[
      chartData.length - 1
    ] as WinRateDrilldownChartDataType;

    // Group by values to detect overlaps, similar to your LineChart component
    const valueGroups = groupBy(
      Object.entries(lastPoint).filter(([key]) => key !== "date"),
      ([_, value]) => value
    );

    return (
      <div className="relative h-full text-secondary pr-4">
        {Object.entries(valueGroups).map(([value, entries]) => {
          const hasOverlap = entries.length > 1;

          return entries.map(([id, _value], index) => {
            const userData = find(dynamicTableData, (user) => user?.id === id);
            if (!userData) return;
            const userColor =
              salesPipelineColors[
                indexOf(dynamicTableData, userData) % salesPipelineColors.length
              ].stroke;

            if (!isEmpty(userData)) {
              return (
                <div
                  key={`${id}-${value}`}
                  className="absolute transition-all"
                  style={{
                    bottom: calculateHeight(Number(value)),
                    left: hasOverlap ? `${index * 6}px` : undefined
                  }}
                  title={`${userData.name}: ${Number(value).toFixed(1)}%`}
                >
                  <UserAvatar
                    size="M"
                    userData={userData}
                    className="border"
                    style={{ borderColor: userColor }}
                  />
                </div>
              );
            }
          });
        })}
      </div>
    );
  }, [chartData, dynamicTableData, calculateHeight]);

  const lineComponents = useMemo(
    () =>
      map(keys(chartData[0]), (dataKey) => {
        if (
          dataKey !== "date" &&
          find(dynamicTableData, (user) => user?.id === dataKey)
        ) {
          const userData = find(
            dynamicTableData,
            (user) => user?.id === dataKey
          );
          const userColor =
            salesPipelineColors[
              indexOf(dynamicTableData, userData) % salesPipelineColors.length
            ].stroke;
          return (
            <Line
              key={uniqueId()}
              type="linear"
              dataKey={dataKey}
              stroke={userColor}
              activeDot={false}
              dot={{ fill: "var(--grey-600)" }}
            />
          );
        }
      }),
    [chartData, dynamicTableData]
  );

  const MemoizedComparisonTooltip = useMemo(() => {
    const ComparisonTooltip = ({
      active,
      payload
    }: TooltipProps<ValueType, NameType>) => {
      if (active && payload && payload.length) {
        return <CustomTooltip data={payload[0].payload} />;
      }
      return null;
    };
    return memo(ComparisonTooltip);
  }, []);

  return (
    <div className="flex w-full h-[250px]">
      <ResponsiveContainer width="100%" height={height} style={{ zIndex: 20 }}>
        <LineChart
          data={chartData}
          margin={{ left: 25, right: 25, top: 5, bottom: 0 }}
          onMouseMove={handleMouseMove}
        >
          <CartesianGrid
            strokeDasharray="5 5"
            stroke="var(--grey-600)"
            horizontal={false}
          />
          <XAxis
            dataKey="date"
            interval={0}
            tickLine={false}
            axisLine={{ stroke: "var(--grey-600)" }}
            tick={{ fill: "var(--secondary)", fontSize: 10 }}
          />
          <YAxis
            tickLine={false}
            axisLine={{ stroke: "var(--grey-600)" }}
            tick={{ fill: "var(--secondary)", fontSize: 12 }}
            domain={[lowerBound, yAxisTicks[yAxisTicks.length - 1]]} // Force start at 0
            ticks={yAxisTicks}
            tickFormatter={(tick) => `${tick}%`}
          />
          {lineComponents}
          <RechartsTooltip
            content={<MemoizedComparisonTooltip />}
            cursor={false}
            position={tooltipPos}
            wrapperStyle={{ visibility: tooltipPos.x ? "visible" : "hidden" }}
          />
          <Legend
            content={() => (
              <div className="absolute -bottom-3 left-1/2 w-full">
                <Text
                  as="div"
                  variant="smBodySecondary"
                >{`${chartData[0].date}-${chartData[chartData.length - 1].date}`}</Text>
              </div>
            )}
            verticalAlign="bottom"
            align="center"
          />
        </LineChart>
      </ResponsiveContainer>
      {renderIcons}
    </div>
  );
});

export default DrilldownChart;
